import { logWarn } from "@/lib/log";
// Generate a shareable, elegant emerald+gold "Zikir" card (pure canvas, no deps)
// and share it via the Web Share API, with WhatsApp + download fallback.
const W = 1080;
const PAD = 90;
const CONTENT_W = W - PAD * 2;

const GOLD = "#E6C25C";
const GOLD_DEEP = "#D4AF37";
const WHITE = "#ffffff";
const SUB = "rgba(255,255,255,0.82)";

export function wrapLines(ctx, text, font, maxW) {
  ctx.font = font;
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxW && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines.length ? lines : [""];
}

export async function shareZikir({ arabic, translit, name, meaning, virtue, source, labels }) {
  try {
    await document.fonts.ready;
    await document.fonts.load('64px "UthmanicHafs"');
  } catch (e) { logWarn("fonts not ready, using system fonts:", e); }

  const m = document.createElement("canvas").getContext("2d");

  // ---- Build the section list with fonts, then measure total height ----
  const AR_FONT = (px) => `${px}px UthmanicHafs, "Traditional Arabic", Georgia, serif`;
  const SERIF = (px, w = "") => `${w} ${px}px "Playfair Display", Georgia, serif`.trim();
  const SANS = (px, w = "") => `${w} ${px}px "Plus Jakarta Sans", Arial, sans-serif`.trim();

  const sections = [];
  // name / title
  sections.push({ type: "lines", lines: wrapLines(m, name, SERIF(52, "bold"), CONTENT_W), font: SERIF(52, "bold"), color: GOLD, lh: 66, gap: 30 });
  // divider
  sections.push({ type: "divider", gap: 40 });
  // arabic
  sections.push({ type: "lines", rtl: true, lines: wrapLines(m, arabic, AR_FONT(66), CONTENT_W), font: AR_FONT(66), color: WHITE, lh: 108, gap: 34 });
  // translit
  sections.push({ type: "lines", lines: wrapLines(m, translit, SANS(30, ""), CONTENT_W), font: `italic 30px "Plus Jakarta Sans", Arial, sans-serif`, color: SUB, lh: 44, gap: 44 });
  // meaning
  sections.push({ type: "lines", lines: wrapLines(m, meaning, SANS(34), CONTENT_W), font: SANS(34), color: WHITE, lh: 50, gap: 40 });
  // virtue label + body (boxed) — only when present
  if (virtue) {
    const vLines = wrapLines(m, virtue, SANS(32), CONTENT_W - 60);
    sections.push({ type: "box", label: labels.virtueLabel, lines: vLines, font: SANS(32), lh: 46, gap: 36 });
  }
  // source — only when present
  if (source) {
    sections.push({ type: "lines", lines: wrapLines(m, `${labels.sourceLabel}: ${source}`, SANS(28), CONTENT_W), font: SANS(28), color: GOLD, lh: 40, gap: 0 });
  }

  const TOP = 320; // header space
  const FOOTER = 150;
  let h = TOP;
  for (const s of sections) {
    if (s.type === "divider") { h += s.gap; continue; }
    if (s.type === "box") { h += 40 + s.lh + s.lines.length * s.lh + 40 + s.gap; continue; }
    h += s.lines.length * s.lh + s.gap;
  }
  h += FOOTER;
  const H = Math.max(1350, Math.ceil(h));

  // ---- Draw ----
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, "#0a3b2b");
  g.addColorStop(0.5, "#0f5a3f");
  g.addColorStop(1, "#0a3b2b");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);

  // subtle radial glow top
  const rg = ctx.createRadialGradient(W - 120, 120, 40, W - 120, 120, 460);
  rg.addColorStop(0, "rgba(230,194,92,0.22)");
  rg.addColorStop(1, "rgba(230,194,92,0)");
  ctx.fillStyle = rg;
  ctx.fillRect(0, 0, W, H);

  ctx.strokeStyle = GOLD_DEEP;
  ctx.lineWidth = 8;
  ctx.strokeRect(44, 44, W - 88, H - 88);
  ctx.lineWidth = 2;
  ctx.strokeRect(64, 64, W - 128, H - 128);

  // header
  ctx.textAlign = "center";
  ctx.fillStyle = GOLD;
  ctx.font = AR_FONT(48);
  ctx.direction = "rtl";
  ctx.fillText("بِسْمِ اللَّهِ", W / 2, 155);
  ctx.direction = "ltr";
  ctx.fillStyle = SUB;
  ctx.font = SANS(26, "");
  ctx.fillText((labels.brand || "Manasik").toUpperCase(), W / 2, 205);

  let y = TOP;
  for (const s of sections) {
    if (s.type === "divider") {
      ctx.strokeStyle = "rgba(230,194,92,0.5)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(W / 2 - 70, y - s.gap / 2);
      ctx.lineTo(W / 2 + 70, y - s.gap / 2);
      ctx.stroke();
      y += s.gap / 2;
      continue;
    }
    if (s.type === "box") {
      const boxTop = y;
      const boxH = 40 + s.lh + s.lines.length * s.lh + 40;
      ctx.fillStyle = "rgba(255,255,255,0.07)";
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(PAD, boxTop, CONTENT_W, boxH, 28);
        ctx.fill();
      } else {
        ctx.fillRect(PAD, boxTop, CONTENT_W, boxH);
      }
      ctx.textAlign = "center";
      ctx.direction = "ltr";
      ctx.fillStyle = GOLD;
      ctx.font = SANS(26, "bold");
      ctx.fillText((s.label || "").toUpperCase(), W / 2, boxTop + 44);
      ctx.fillStyle = WHITE;
      ctx.font = s.font;
      let by = boxTop + 44 + s.lh;
      for (const ln of s.lines) { ctx.fillText(ln, W / 2, by); by += s.lh; }
      y += boxH + s.gap;
      continue;
    }
    ctx.textAlign = "center";
    ctx.direction = s.rtl ? "rtl" : "ltr";
    ctx.fillStyle = s.color;
    ctx.font = s.font;
    for (const ln of s.lines) { ctx.fillText(ln, W / 2, y); y += s.lh; }
    y += s.gap;
  }

  // footer
  ctx.direction = "ltr";
  ctx.textAlign = "center";
  ctx.fillStyle = SUB;
  ctx.font = SANS(28, "");
  ctx.fillText(labels.footer || "Manasik — Panduan Umrah & Haji", W / 2, H - 80);

  const blob = await new Promise((r) => canvas.toBlob(r, "image/png"));
  const file = new File([blob], "zikir.png", { type: "image/png" });
  const shareText = labels.shareText || "";

  try {
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: name, text: shareText });
      return "shared";
    }
  } catch (e) { logWarn("Web Share cancelled/unsupported, falling back:", e); }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "zikir-manasik.png";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`, "_blank");
  return "downloaded";
}
